import React, { useRef, useState } from 'react';
import { Input, Select, SolidButton } from '@riac/design-system';
import { Item } from '@react-stately/collections';
import { useTurnstile, useTurnstileRender } from '@riac/core/turnstile';

interface IAddCreditCardCountry {
  CountryID: number;
  CountryName: string;
  CountryCode: string;
}

export interface IAddCreditCardFormProps {
  countries: IAddCreditCardCountry[];
  onAddCard: () => void;
  inverted?: boolean;
}

interface FormData {
  cardholderName: string;
  cardNumber: string;
  expiration: string;
  cvv: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  cardNickname: string;
}

interface FormErrors {
  cardholderName?: string;
  cardNumber?: string;
  expiration?: string;
  cvv?: string;
  country?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  cardNickname?: string;
}

export const AddCreditCardForm = ({ countries, onAddCard, inverted }: IAddCreditCardFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    cardholderName: '',
    cardNumber: '',
    expiration: '',
    cvv: '',
    country: '236', // Default to US
    address: '',
    city: '',
    state: '',
    zipCode: '',
    cardNickname: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [verify, setVerify] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [authErrors, setAuthErrors] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const [turnstileToken, setTurnstileToken] = useState<any>();

  const turnstile = useTurnstile();
  const turnstileContainerRef = useRef();

  const widgetManager = useTurnstileRender(
    turnstile,
    window.TURNSTILE_SITE_KEY,
    turnstileContainerRef,
    (token) => setTurnstileToken(token),
  );

  const handleChange = (name, value) => {
    let formattedValue = value;

    if (name === 'cardNumber') {
      // Remove all non-digit characters
      formattedValue = value.replace(/\D/g, '');
      
      // Check if it's an AMEX card (starts with 34 or 37)
      const isAmex = /^3[47]/.test(formattedValue);
      
      if (isAmex) {
        // Format AMEX: XXXX XXXXXX XXXXX
        formattedValue = formattedValue.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
      } else {
        // Format other cards: XXXX XXXX XXXX XXXX
        formattedValue = formattedValue.replace(/(\d{4})(?=\d)/g, '$1 ');
      }
      formattedValue = formattedValue.trim();
    } else if (name === 'expiration') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 2) {
        formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2, 4);
      }
    } else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: formattedValue,
    }));
  };

  const validatePostalCode = (postalCode: string, countryCode: string): boolean => {
    const postalCodeRegexes = {
      US: /^\d{5}(-\d{4})?$/,
      CA: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/,
      GB: /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/,
    };

    // We could specify more countries here e.g. from https://github.com/unicode-org/cldr/blob/release-26-0-1/common/supplemental/postalCodeData.xml
    // But I think it's better to keep it simple. w/ a general regex for other countries and allowing blank postal codes for them.

    const defaultRegex = /^[A-Z0-9 -]{1,10}$/i;
    if(!postalCodeRegexes[countryCode] && (!postalCode || postalCode.length === 0)) {
      return true;
    }

    const regex = postalCodeRegexes[countryCode] || defaultRegex;
    return regex.test(postalCode);
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;

    if (!formData.cardholderName.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    }
    
    const cardNumber = formData.cardNumber.replace(/\s/g, '');
    const isAmex = /^3[47]\d{13}$/.test(cardNumber);
    const isOtherCard = /^\d{16}$/.test(cardNumber);
    if (!isAmex && !isOtherCard) {
      newErrors.cardNumber = 'Invalid card number';
    }

    if (!/^\d{2}\/\d{2}$/.test(formData.expiration)) {
      newErrors.expiration = 'Invalid expiration date (MM/YY)';
    } else {
      const [month, year] = formData.expiration.split('/').map(Number);
      if (month < 1 || month > 12) {
        newErrors.expiration = 'Invalid month';
      } else if (year < currentYear || (year === currentYear && month < currentMonth)) {
        newErrors.expiration = 'Card has expired';
      }
    }

    // Validate CVV (3 digits for regular cards, 4 for AMEX)
    const cvvRegex = isAmex ? /^\d{4}$/ : /^\d{3}$/;
    if (!cvvRegex.test(formData.cvv)) {
      newErrors.cvv = `Invalid CVV (${isAmex ? '4' : '3'} digits required)`;
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }
    if (!formData.address) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    const selectedCountry = countries.find(c => c.CountryID.toString() === formData.country);
    const countryCode = selectedCountry ? selectedCountry.CountryCode : 'US';

    if (!validatePostalCode(formData.zipCode, countryCode)) {
      newErrors.zipCode = 'Invalid postal code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addCard = () => {
    const creditCardNumber = formData.cardNumber.replace(/\s/g, '');
    const cc4 = creditCardNumber.slice(-4);
    const validated = validateForm();
    if (!validated) {
      return;
    }
    setAuthErrorMessage('');
    setAuthErrors(false);
    setVerify(true);
    fetch('/api/account/creditcard/addCard', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fullName: formData.cardholderName,
        cardNumber: creditCardNumber,
        cardExpiration: formData.expiration,
        cardCode: formData.cvv,
        streetAddress: formData.address,
        city: formData.city,
        state: formData.state,
        postalCode: formData.zipCode,
        last4: cc4,
        cardNickname: formData.cardNickname,
        country: formData.country,
        turnstileToken,
      }),
    }).then((response) => response.json())
      .then((e) => {
        if (e.success) {
          setVerify(false);
          setConfirm(true);
          setFormData({
            cardholderName: '',
            cardNumber: '',
            expiration: '',
            cvv: '',
            country: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            cardNickname: '',
          });
          console.log(e);
          onAddCard();
        } else {
          setAuthErrorMessage(e.message);
          setVerify(false);
          setConfirm(false);
          setAuthErrors(true);
          let message = e.message + ', please confirm your card information';
          if (typeof e.errors !== 'undefined') {
            message = e.message;
          }
          setAuthErrorMessage(message);
          widgetManager.reset();
        }
      });
  };

  return (
    <form onSubmit={addCard}>
      <div className='flex flex-col gap-1' style={{maxWidth: '540px'}}>
        <div>
          <Input
            label='Card Nickname (Optional)'
            name='cardNickname'
            value={formData.cardNickname}
            onChange={(value) => handleChange('cardNickname', value)}
            disabled={verify}
            inverted={inverted}
          />
        </div>
        <div>
          <Input
            label='Cardholder Name'
            name='cardholderName'
            value={formData.cardholderName}
            onChange={(value) => handleChange('cardholderName', value)}
            disabled={verify}
            inverted={inverted}
          />
          {errors.cardholderName && <span className="text-red-500">{errors.cardholderName}</span>}
        </div>
        <div>
          <Input
            id='cardNumber'
            name='cardNumber'
            label='Card Number'
            value={formData.cardNumber}
            onChange={(e) => handleChange('cardNumber', e)}
            maxLength={19}
            disabled={verify}
            inverted={inverted}
          />
          {errors.cardNumber && <span className="text-red-500">{errors.cardNumber}</span>}
        </div>
        <div className='flex gap-4'>
          <div>
            <Input
              label='Expiration (MM/YY)'
              name='expiration'
              value={formData.expiration}
              onChange={(value) => handleChange('expiration', value)}
              maxLength={5}
              disabled={verify}
              inverted={inverted}
            />
            {errors.expiration && <span className="text-red-500">{errors.expiration}</span>}
          </div>
          <div className='w-52'>
            <Input
              label='CVV'
              name='cvv'
              value={formData.cvv}
              onChange={(value) => handleChange('cvv', value)}
              disabled={verify}
              inverted={inverted}
            />
            {errors.cvv && <span className="text-red-500">{errors.cvv}</span>}
          </div>
        </div>
        <div>
          <Input
            label='Address'
            name='address'
            value={formData.address}
            onChange={(value) => handleChange('address', value)}
            disabled={verify}
            inverted={inverted}
          />
          {errors.address && <span className="text-red-500">{errors.address}</span>}
        </div>
        <div className='flex gap-4'>
          <div>
            <Input
              label='City'
              name='city'
              value={formData.city}
              onChange={(value) => handleChange('city', value)}
              disabled={verify}
              inverted={inverted}
            />
            {errors.city && <span className="text-red-500">{errors.city}</span>}
          </div>
          <div>
            <Input
              label='State/Province'
              name='state'
              value={formData.state}
              onChange={(value) => handleChange('state', value)}
              disabled={verify}
              inverted={inverted}
            />
            {errors.state && <span className="text-red-500">{errors.state}</span>}
          </div>
          <div>
            <Input
              label='Postal Code'
              name='zipCode'
              value={formData.zipCode}
              onChange={(value) => handleChange('zipCode', value)}
              disabled={verify}
              inverted={inverted}
            />
            {errors.zipCode && <span className="text-red-500">{errors.zipCode}</span>}
          </div>
        </div>
        <div className='flex flex-col'>
          <Select
            label='Country'
            name='country'
            value={formData.country}
            onChange={(key) => handleChange('country', key)}
            isDisabled={verify}
            inverted={inverted}
            position={'top'}
          >
            {countries.map((country) => (
              <Item key={country.CountryID}>
                {country.CountryName}
              </Item>
            ))}
          </Select>
          {errors.country && <span className="text-red-500">{errors.country}</span>}
        </div>
        <div className='flex flex-col gap-2'>
          <div ref={turnstileContainerRef}></div>
          <SolidButton onPress={addCard} isDisabled={verify}>
            {verify ? 'Verifying...' : 'Add Card'}
          </SolidButton>
          {Object.keys(errors).length > 0 && <span className="text-red-500">Please fix the errors above</span>}
          {authErrors && <span className="text-red-500">{authErrorMessage}</span>}
          {confirm && <span className="text-green-500">Card added successfully!</span>}
        </div>
      </div>
    </form>
  );
};
